import { useEffect, useState, useRef } from "react";
import { Instagram, ChevronLeft, ChevronRight, X } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getEmbedUrl = (postUrl) => {
  if (!postUrl) return null;
  const clean = postUrl.split("?")[0].replace(/\/$/, "");
  return `${clean}/embed/`;
};

const LazyEmbed = ({ post, onClick }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const embedUrl = getEmbedUrl(post.post_url);

  return (
    <div
      ref={ref}
      className="flex-shrink-0 snap-start bg-white overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#C9A85A] transition-all duration-300"
      style={{ width: 250, height: 320 }}
      onClick={onClick}
    >
      {visible && embedUrl ? (
        <iframe
          src={embedUrl}
          className="w-full border-0 pointer-events-none"
          style={{ height: 320 }}
          allowTransparency="true"
          scrolling="no"
          title={post.caption || "Instagram post"}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-[#191646] flex items-center justify-center">
          <Instagram className="text-[#C9A85A]/30" size={32} />
        </div>
      )}
    </div>
  );
};

export const InstaFeed = () => {
  const [posts, setPosts] = useState([]);
  const [popupPost, setPopupPost] = useState(null);
  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const [sectionVisible, setSectionVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setSectionVisible(true); observer.disconnect(); } },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!sectionVisible) return;
    const fetchPosts = async () => {
      try {
        await axios.post(`${API}/seed-instagram`).catch(() => {});
        const res = await axios.get(`${API}/instagram`);
        setPosts(res.data);
      } catch (e) {
        console.error("Failed to load Instagram posts", e);
      }
    };
    fetchPosts();
  }, [sectionVisible]);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === "left" ? -280 : 280, behavior: "smooth" });
    }
  };

  return (
    <section ref={sectionRef} id="instagram" className="py-16 md:py-20 bg-[#191646]" data-testid="insta-feed-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <a href="https://www.instagram.com/kiplingsofficial" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#C9A85A] hover:text-white transition-colors">
              <Instagram size={16} />
              <span className="text-xs tracking-[0.2em] uppercase font-body font-semibold">@kiplingsofficial</span>
            </a>
            <span className="text-white/30 text-xs font-body hidden md:inline">Follow the Journey</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scroll("left")} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-[#C9A85A] text-white/50 hover:text-[#C9A85A] transition-colors"><ChevronLeft size={14} /></button>
            <button onClick={() => scroll("right")} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-[#C9A85A] text-white/50 hover:text-[#C9A85A] transition-colors"><ChevronRight size={14} /></button>
            <a href="https://www.instagram.com/kiplingsofficial" target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white px-4 py-1.5 font-body font-semibold text-xs tracking-wider uppercase hover:opacity-90 transition-opacity ml-2">Follow</a>
          </div>
        </div>

        {posts.length > 0 ? (
          <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {posts.map((post, index) => (
              <LazyEmbed key={post.id || index} post={post} onClick={() => setPopupPost(post)} />
            ))}
          </div>
        ) : (
          <div className="flex gap-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="flex-shrink-0 bg-white/5 animate-pulse" style={{ width: 250, height: 320 }} />
            ))}
          </div>
        )}
      </div>

      {popupPost && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="insta-popup">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setPopupPost(null)} />
          <div className="relative bg-white max-w-lg w-full max-h-[90vh] overflow-hidden">
            <button onClick={() => setPopupPost(null)} className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/50 text-white flex items-center justify-center hover:bg-black/80 transition-colors"><X size={16} /></button>
            <iframe src={getEmbedUrl(popupPost.post_url)} className="w-full border-0" style={{ height: "80vh", maxHeight: 700 }} allowTransparency="true" scrolling="yes" title={popupPost.caption || "Instagram post"} />
          </div>
        </div>
      )}
    </section>
  );
};
